import { useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import {
  User, Save, AlertCircle, CheckCircle, LogIn,
  Settings, ChevronRight, ShieldCheck, ExternalLink, CreditCard,
  Trash2, ShieldAlert, BadgeCheck,
  Upload, Calendar, FileText, Clock, XCircle, Star, ImageIcon, Info, Key,
  Lock, LockOpen, Loader2,
} from 'lucide-react';
import { createApiClient, profileService, dvlaService, documentsService, eligibilityService, depositService } from '../services/api';
import type { QuoteEligibilityResult } from '../services/api';
import type { UpdateProfileRequest, UserProfileDto, DepositBankTransferInstructionDto } from '../types';
import type { DvlaStatusResponse } from '../services/api';
import { DocumentType } from '../types';
import type { DocumentUpload } from '../types';

const schema = z.object({
  firstName:             z.string().min(1, 'First name is required'),
  lastName:              z.string().min(1, 'Last name is required'),
  displayName:           z.string().optional(),
  phoneNumber:           z.string().optional(),
  addressLine1:          z.string().optional(),
  addressLine2:          z.string().optional(),
  city:                  z.string().optional(),
  postcode:              z.string().optional(),
  isBusinessAccount:     z.boolean(),
  businessName:          z.string().optional(),
  vatNumber:             z.string().optional(),
  companiesHouseNumber:  z.string().optional(),
  directorName:          z.string().optional(),
  preferredAreas:        z.string().optional(),
  voiceChatEnabled:      z.boolean().default(false),
  bankAccountName:       z.string().optional(),
  bankSortCode:          z.string().optional(),
  bankAccountNumber:     z.string().optional(),
  stripeConnectedAccountId: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;


type DocRole = 'Carrier' | 'Workshop';

function StatusBadge({ status }: { status: string }) {
  if (status === 'Approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 shrink-0">
      <CheckCircle className="w-3 h-3" />Approved
    </span>
  );
  if (status === 'Rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 shrink-0">
      <XCircle className="w-3 h-3" />Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 shrink-0">
      <Clock className="w-3 h-3" />Pending Review
    </span>
  );
}

// ── External Ratings ───────────────────────────────────────────────────────────

interface ScreenshotUpload {
  uploadId: string;
  platform: string;
  userRole: string;
  originalFileName: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedRating: number | null;
  approvedReferenceText: string | null;
  rejectionReason: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
  screenshotSasUrl: string | null;
  aiExtractedRating: number | null;
}

const RATING_PLATFORMS = ['Trustpilot', 'Google', 'Other'] as const;
type RatingPlatform = (typeof RATING_PLATFORMS)[number];

const PLATFORM_HINTS: Record<RatingPlatform, string> = {
  Trustpilot:  'Screenshot your Trustpilot business page showing your TrustScore and review count.',
  Google:      'Screenshot your Google Business profile showing your star rating and review count.',
  Other:       'Screenshot any verifiable external ratings page showing your business name and rating.',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
      ))}
      <span className="ml-1 text-sm font-semibold text-gray-700">{(rating ?? 0).toFixed(1)}</span>
    </span>
  );
}

function ExternalRatingsSection({ getToken, userRole }: {
  getToken: () => Promise<string>;
  userRole: DocRole;
}) {
  const [platform, setPlatform]         = useState<RatingPlatform>('Trustpilot');
  const [otherDetails, setOtherDetails] = useState('');
  const [file, setFile]                 = useState<File | null>(null);
  const [uploading, setUploading]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [success, setSuccess]           = useState(false);
  const [aiRating, setAiRating]         = useState<number | null>(null);
  const fileInputRef                    = useRef<HTMLInputElement>(null);

  const { data: uploads = [], isLoading, refetch } = useQuery<ScreenshotUpload[]>({
    queryKey: ['my-rating-screenshots'],
    queryFn: async () => {
      const r = await createApiClient(getToken).get<ScreenshotUpload[]>('/api/v1/ratings/screenshots/my');
      return r.data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a screenshot.'); return; }
    if (platform === 'Other' && !otherDetails.trim()) {
      setError('Please describe the platform in the box below.');
      return;
    }
    setUploading(true); setError(null); setSuccess(false);
    try {
      const form = new FormData();
      form.append('screenshot', file);
      form.append('platform', platform);
      form.append('userRole', userRole);
      if (platform === 'Other') form.append('otherPlatformDetails', otherDetails.trim());
      const res = await createApiClient(getToken).post<ScreenshotUpload>(
        '/api/v1/ratings/screenshots', form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setAiRating(res.data?.aiExtractedRating ?? null);
      setSuccess(true); setFile(null); setOtherDetails('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { title?: string } }; message?: string };
      setError(e?.response?.data?.title ?? e?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-gray-100">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">External Rating Verification</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload a screenshot of your Checkatrade, Trustpilot, or Google rating.
            Support will verify it and display your approved rating on your public profile.
            {uploads.length === 0 && !isLoading && (
              <> This is a one-time setup — complete it now to build instant credibility with customers.</>
            )}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin w-5 h-5 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : uploads.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-100 space-y-3">
          {uploads.map(u => (
            <div key={u.uploadId} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{u.platform}</span>
                  <span className="text-xs text-gray-400">{new Date(u.uploadedAt).toLocaleDateString('en-GB')}</span>
                  {u.screenshotSasUrl && (
                    <a href={u.screenshotSasUrl} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-500 hover:text-blue-700 underline">
                      View screenshot
                    </a>
                  )}
                </div>
                {u.status === 'Approved' && u.approvedRating != null && (
                  <div className="mt-1"><StarRating rating={u.approvedRating} /></div>
                )}
                {u.status === 'Approved' && u.approvedReferenceText && (
                  <p className="text-xs text-gray-500 italic mt-0.5">"{u.approvedReferenceText}"</p>
                )}
                {u.status === 'Rejected' && u.rejectionReason && (
                  <p className="text-xs text-red-600 mt-0.5">Rejected: {u.rejectionReason} — please re-upload a clearer screenshot.</p>
                )}
              </div>
              <StatusBadge status={u.status} />
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
        {uploads.length > 0 && (
          <p className="text-xs text-gray-500 font-medium">Add another platform:</p>
        )}
        <div className="grid grid-cols-3 gap-2">
          {RATING_PLATFORMS.map(p => (
            <button key={p} type="button" onClick={() => { setPlatform(p); setOtherDetails(''); setError(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                platform === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}>
              {p}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">{PLATFORM_HINTS[platform]}</p>

        {platform === 'Other' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Platform name / description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="form-input w-full text-sm"
              placeholder="e.g. Trustindex, Bark.com, Yell.com…"
              value={otherDetails}
              maxLength={200}
              onChange={e => { setOtherDetails(e.target.value); setError(null); }}
            />
          </div>
        )}

        <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
          <div
            className="flex-1 min-w-0 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {file ? (
              <span className="text-gray-700 font-medium truncate">{file.name}</span>
            ) : (
              <span className="text-gray-400">Choose screenshot (PNG / JPEG, max 5 MB)</span>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setSuccess(false); }} />
          <button type="submit" disabled={uploading || !file}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5">
            {uploading ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? 'Uploading…' : 'Submit'}
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
          </div>
        )}
        {success && (
          <div className="flex flex-col gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Submitted for review. Our support team will verify it within 24 hours.
            </div>
            {aiRating != null && (
              <div className="flex items-center gap-1.5 pl-5">
                <Info className="w-3 h-3 text-green-600 shrink-0" />
                <span className="text-green-600">
                  AI detected a rating of <strong>{aiRating.toFixed(1)}</strong> from your screenshot — your image is clear.
                </span>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

function profileCompletion(p: UserProfileDto): number {
  const checks = [
    !!p.firstName, !!p.lastName, !!p.phoneNumber,
    !!p.addressLine1, !!p.city, !!p.postcode,
  ];
  return Math.round(checks.filter(Boolean).length / checks.length * 100);
}

export default function ProfilePage() {
  const { account, getToken, login, logout, changePassword } = useAppAuth();
  const queryClient  = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const returnTo       = searchParams.get('returnTo');
  const creditRequired = searchParams.get('creditRequired') === '1';
  const depositStatus  = searchParams.get('deposit') as 'success' | 'cancelled' | null;

  const api   = createApiClient(getToken);
  const svc   = profileService(api);
  const dvla  = dvlaService(api);
  const eligSvc = eligibilityService(api);

  const [shareCode,      setShareCode]      = useState('');
  const [shareCodeError, setShareCodeError] = useState<string | null>(null);
  const [shareCodeDone,  setShareCodeDone]  = useState(false);

  const [refereeCode,        setRefereeCode]        = useState('');
  const [refereeCodeError,   setRefereeCodeError]   = useState<string | null>(null);
  const [refereeCodeSuccess, setRefereeCodeSuccess] = useState(false);

  const [identityDocType,   setIdentityDocType]   = useState('');
  const [identityDocNumber, setIdentityDocNumber] = useState('');
  const [identityDocFile,   setIdentityDocFile]   = useState<File | null>(null);
  const [identityError,     setIdentityError]     = useState<string | null>(null);
  const [identityUploading, setIdentityUploading] = useState(false);
  const [identitySubmitted, setIdentitySubmitted] = useState(false);
  const identityFileRef = useRef<HTMLInputElement>(null);

  const [licFrontFile, setLicFrontFile] = useState<File | null>(null);
  const [licBackFile,  setLicBackFile]  = useState<File | null>(null);
  const [licUploading, setLicUploading] = useState(false);
  const [licError,     setLicError]     = useState<string | null>(null);
  const [licSubmitted, setLicSubmitted] = useState(false);
  const licFrontFileRef = useRef<HTMLInputElement>(null);
  const licBackFileRef  = useRef<HTMLInputElement>(null);

  const avatarFileRef = useRef<HTMLInputElement>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const avatarMutation = useMutation({
    mutationFn: (file: File) => profileService(api).uploadAvatar(file),
    onSuccess: () => {
      setAvatarError(null);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (e: any) => {
      setAvatarError(e?.response?.data ?? e?.message ?? 'Upload failed. Please try again.');
    },
  });

  const { data: profile, isLoading, error: loadError } = useQuery({
    queryKey: ['profile'],
    queryFn: () => svc.getProfile(),
    enabled: !!account,
  });

  const { data: dvlaStatus, refetch: refetchDvlaStatus } = useQuery<DvlaStatusResponse>({
    queryKey: ['dvla', 'status'],
    queryFn: () => dvla.getStatus(),
    enabled: !!account,
  });


  const { data: eligibility, refetch: refetchEligibility } = useQuery<QuoteEligibilityResult>({
    queryKey: ['eligibility'],
    queryFn: () => eligSvc.getMyEligibility(),
    enabled: !!account && (profile?.role?.toLowerCase() === 'carrier' || profile?.role?.toLowerCase() === 'workshop'),
  });

  const { data: myDocs = [], refetch: refetchMyDocs } = useQuery<DocumentUpload[]>({
    queryKey: ['my-documents'],
    queryFn: () => documentsService(api).getMy(),
    enabled: !!account && (
      profile?.role?.toLowerCase() === 'carrier' ||
      profile?.role?.toLowerCase() === 'customer'
    ),
  });

  const licDocs = myDocs.filter(d => d.documentType === DocumentType.DrivingLicence);
  const licLatest = licDocs.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())[0];

  const identityDocs = myDocs.filter(d => d.documentType === DocumentType.IdentityDocument);
  const identityLatest = identityDocs.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())[0];

  const submitLicence = async () => {
    if (!licFrontFile) { setLicError('Please select the front of your licence.'); return; }
    setLicUploading(true); setLicError(null);
    try {
      const svc = documentsService(api);
      await svc.upload(licFrontFile, DocumentType.DrivingLicence, 'Carrier');
      if (licBackFile) await svc.upload(licBackFile, DocumentType.DrivingLicence, 'Carrier');
      setLicFrontFile(null); setLicBackFile(null);
      setLicSubmitted(true);
      if (licFrontFileRef.current) licFrontFileRef.current.value = '';
      if (licBackFileRef.current)  licBackFileRef.current.value  = '';
      refetchMyDocs();
    } catch (e: any) {
      setLicError(e?.response?.data?.error ?? e?.message ?? 'Upload failed. Please try again.');
    } finally {
      setLicUploading(false);
    }
  };

  const referralCodeMutation = useMutation({
    mutationFn: (code: string) => eligSvc.enterReferralCode(code),
    onSuccess: () => {
      setRefereeCode('');
      setRefereeCodeError(null);
      setRefereeCodeSuccess(true);
      refetchEligibility();
    },
    onError: (e: any) => {
      setRefereeCodeError(e?.response?.data?.error ?? e?.message ?? 'Code not recognised. Please check and try again.');
      setRefereeCodeSuccess(false);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => svc.deleteAccount(),
    onSuccess: () => { logout(); },
    onError: (e: any) => {
      alert(e?.response?.data?.error ?? e?.message ?? 'Could not delete your account. Please try again.');
    },
  });

  const handleDeleteAccount = () => {
    if (!window.confirm(
      'Delete your account? Your account will be deactivated immediately and you will not be able to ' +
      'sign in again, even with the same email address. This cannot be undone by you — only an admin can ' +
      'restore it. Continue?'
    )) return;
    deleteAccountMutation.mutate();
  };

  const depositCheckoutMutation = useMutation({
    mutationFn: () => eligSvc.createDepositCheckout(),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (e: any) => {
      alert(e?.response?.data?.error ?? 'Could not start checkout. Please try again.');
    },
  });

  // What the deposit costs each way. Both figures come from PlatformFeeConfig rules, so the
  // card fee can be re-rated or switched off without touching this screen.
  const { data: depositQuote } = useQuery({
    queryKey: ['deposit-quote'],
    queryFn: () => depositService(createApiClient(getToken)).getQuote(),
    enabled: !eligibility?.hasSecurityDeposit,
  });

  const [bankTransferInstruction, setBankTransferInstruction] =
    useState<DepositBankTransferInstructionDto | null>(null);

  const depositBankTransferMutation = useMutation({
    mutationFn: () => depositService(createApiClient(getToken)).declareBankTransfer(),
    onSuccess: (instruction) => setBankTransferInstruction(instruction),
    onError: (e: any) => {
      alert(e?.response?.data?.error ?? 'Could not set up the bank transfer. Please try again.');
    },
  });


  const shareCodeMutation = useMutation({
    mutationFn: (code: string) => dvla.submitShareCode(code),
    onSuccess: () => {
      setShareCodeError(null);
      setShareCode('');
      refetchDvlaStatus();
    },
    onError: (e: any) => {
      setShareCodeError(e?.response?.data?.error ?? e?.message ?? 'Submission failed. Please try again.');
    },
  });

  const {
    register, handleSubmit, watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: profile
      ? {
          firstName:            profile.firstName,
          lastName:             profile.lastName,
          displayName:          profile.displayName ?? '',
          phoneNumber:          profile.phoneNumber ?? '',
          addressLine1:         profile.addressLine1 ?? '',
          addressLine2:         profile.addressLine2 ?? '',
          city:                 profile.city ?? '',
          postcode:             profile.postcode ?? '',
          isBusinessAccount:    profile.isBusinessAccount,
          businessName:         profile.businessName ?? '',
          vatNumber:            profile.vatNumber ?? '',
          companiesHouseNumber: profile.companiesHouseNumber ?? '',
          directorName:         profile.directorName ?? '',
          preferredAreas:       profile.preferredAreas ?? '',
          voiceChatEnabled:     profile.voiceChatEnabled ?? false,
          bankAccountName:      profile.bankAccountName ?? '',
          bankSortCode:         profile.bankSortCode ?? '',
          bankAccountNumber:    profile.bankAccountNumber ?? '',
          stripeConnectedAccountId: profile.stripeConnectedAccountId ?? '',
        }
      : undefined,
  });

  const isCarrier   = profile?.role?.toLowerCase() === 'carrier';
  const isWorkshop  = profile?.role?.toLowerCase() === 'workshop';
  const isCustomer  = profile?.role?.toLowerCase() === 'customer';
  const docUserRole: DocRole = isWorkshop ? 'Workshop' : 'Carrier';
  const isBusiness  = watch('isBusinessAccount');


  const submitAll = async () => {
    const hasDoc  = !!(identityDocType && identityDocFile);
    const hasCode = !!(identityDocType === 'DrivingLicence' && shareCode.trim());
    if (!identityDocType) { setIdentityError('Please select a document type.'); return; }
    if (!hasDoc && !hasCode) { setIdentityError('Please attach a document file or enter a share code.'); return; }
    setIdentityUploading(true);
    setIdentityError(null);
    try {
      if (hasDoc) {
        await documentsService(api).upload(identityDocFile!, DocumentType.IdentityDocument, 'Customer');
        await svc.submitIdentityDocument(identityDocType, identityDocNumber || undefined);
        setIdentityDocType('');
        setIdentityDocNumber('');
        setIdentityDocFile(null);
        setIdentitySubmitted(true);
        if (identityFileRef.current) identityFileRef.current.value = '';
        refetchMyDocs();
      }
      if (hasCode) {
        await dvla.submitShareCode(shareCode);
        setShareCode('');
        setShareCodeDone(true);
        refetchDvlaStatus();
      }
    } catch (e: any) {
      setIdentityError(e?.response?.data?.error ?? e?.message ?? 'Submission failed.');
    } finally {
      setIdentityUploading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => svc.updateProfile(data),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['profile'], updatedProfile);
      if (returnTo && updatedProfile.isCreditPaymentEligible) navigate(returnTo);
    },
  });

  const [creditError, setCreditError] = useState<string | null>(null);
  const creditMutation = useMutation({
    mutationFn: () => svc.requestCredit(),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['profile'], updatedProfile);
      setCreditError(null);
    },
    onError: (e: any) => {
      setCreditError(e?.response?.data?.error ?? e?.message ?? 'Request failed. Please try again.');
    },
  });

  const onSubmit = (values: FormValues) => {
    mutation.mutate({
      firstName:            values.firstName,
      lastName:             values.lastName,
      displayName:          values.displayName || undefined,
      phoneNumber:          values.phoneNumber || undefined,
      addressLine1:         values.addressLine1 || undefined,
      addressLine2:         values.addressLine2 || undefined,
      city:                 values.city || undefined,
      postcode:             values.postcode || undefined,
      isBusinessAccount:    values.isBusinessAccount,
      businessName:         values.businessName || undefined,
      vatNumber:            values.vatNumber || undefined,
      companiesHouseNumber: values.companiesHouseNumber || undefined,
      directorName:         values.directorName || undefined,
      preferredAreas:       values.preferredAreas || undefined,
      voiceChatEnabled:         values.voiceChatEnabled,
      bankAccountName:          values.bankAccountName || undefined,
      bankSortCode:             values.bankSortCode || undefined,
      bankAccountNumber:        values.bankAccountNumber || undefined,
      stripeConnectedAccountId: values.stripeConnectedAccountId || undefined,
    });
  };

  if (!account) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card p-10 text-center max-w-sm w-full">
          <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Sign in to view your profile</h2>
          <p className="text-sm text-gray-500 mb-6">You need to be signed in to access your profile.</p>
          <button onClick={() => login()} className="btn-primary w-full flex items-center justify-center gap-2">
            <LogIn className="w-4 h-4" /> Sign In
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-blue-700 font-medium">Loading your profile…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-sm w-full">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">Failed to load profile</p>
          <p className="text-sm text-gray-500 mt-1">{(loadError as Error).message}</p>
        </div>
      </div>
    );
  }

  const pct = profile ? profileCompletion(profile) : 0;
  return (
    <div className="page-shell">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        {/* Avatar with upload overlay */}
        <div className="relative shrink-0 group">
          <div
            className="w-14 h-14 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center cursor-pointer"
            onClick={() => avatarFileRef.current?.click()}
          >
            {profile?.avatarUrl ? (
              <img src={profile.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
            ) : (() => {
              const initials = (profile?.firstName?.[0] ?? '') + (profile?.lastName?.[0] ?? '');
              return initials ? (
                <span className="text-xl font-bold text-blue-600 select-none">{initials.toUpperCase()}</span>
              ) : (
                <User className="w-6 h-6 text-blue-600" />
              );
            })()}
          </div>
          <button
            type="button"
            onClick={() => avatarFileRef.current?.click()}
            className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow hover:bg-indigo-700 transition-colors"
            title="Upload photo"
          >
            {avatarMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Upload className="w-2.5 h-2.5" />
            )}
          </button>
          <input
            ref={avatarFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) { setAvatarError(null); avatarMutation.mutate(file); }
              e.target.value = '';
            }}
          />
        </div>

        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
              {profile?.role}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              profile?.registrationStatus === 'Verified'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {profile?.registrationStatus}
            </span>
          </div>
          {avatarError && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 shrink-0" />{avatarError}
            </p>
          )}
        </div>
      </div>

      {/* Completion banner — only shown to unverified users; once admin has verified, no longer needed */}
      {pct < 100 && profile?.registrationStatus !== 'Verified' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-800">{pct}% complete</span>
            <span className="text-xs text-blue-600">Fill in all sections to unlock full access</span>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div className="h-2 bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* Quick nav */}
      <div className="grid grid-cols-1 gap-3 mb-6">
        <Link to="/preferences" className="card p-3 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors group">
          <Settings className="w-5 h-5 text-indigo-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800">My Preferences</p>
            <p className="text-xs text-gray-400 truncate">Voice, alerts &amp; notifications</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 ml-auto shrink-0 group-hover:text-indigo-400 transition-colors" />
        </Link>
      </div>

      {/* Credit required banner */}
      {creditRequired && !profile?.isCreditPaymentEligible && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-3 mb-5 text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Credit payment requires Admin approval</p>
            <p className="mt-0.5 text-xs text-amber-700">
              {profile?.creditApprovalStatus === 'Pending'
                ? 'Your Credit request is pending review. We will notify you once approved.'
                : profile?.canRequestCredit
                  ? 'You are eligible to request Credit — scroll down to the Credit Account section.'
                  : profile?.isBusinessAccount
                    ? 'Complete your business details below (company name, registration number, VAT number, director name, and address) then request Credit.'
                    : 'Submit and have a government-issued identity document verified, then request Credit in the section below.'}
            </p>
          </div>
        </div>
      )}

      {/* Profile save banners */}
      {mutation.isSuccess && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-5 text-sm">
          <CheckCircle className="w-4 h-4 shrink-0" /> Profile updated successfully.
        </div>
      )}
      {mutation.isError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {(mutation.error as Error).message}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Account info */}
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Account</h2>
          <div className="space-y-4">
            <div>
              <label className="form-label">Email</label>
              <input className="form-input bg-gray-50 cursor-not-allowed" value={profile?.email ?? ''} disabled readOnly />
              <p className="text-xs text-gray-400 mt-1">Managed by your identity provider.</p>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Password</p>
                <p className="text-xs text-gray-400">You will be redirected to your identity provider to change your password.</p>
              </div>
              <button
                type="button"
                onClick={() => changePassword()}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2"
              >
                Change Password
              </button>
            </div>
          </div>
        </section>

        {/* Personal details */}
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Personal Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">First Name</label>
              <input className="form-input" {...register('firstName')} />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="form-label">Last Name</label>
              <input className="form-input" {...register('lastName')} />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>}
            </div>
            <div>
              <label className="form-label">Phone Number <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" type="tel" placeholder="e.g. 07700 900123" {...register('phoneNumber')} />
            </div>
            <div className="sm:col-span-2">
              <label className="form-label">Display Name <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                className="form-input disabled:bg-gray-50 disabled:cursor-not-allowed"
                maxLength={30}
                placeholder="e.g. RapidMover42"
                disabled={!!profile?.displayName}
                {...register('displayName')}
              />
              <p className="text-xs text-gray-400 mt-1">
                {profile?.displayName
                  ? 'Shown instead of your name on listings, reviews and quotes. Cannot be changed once set.'
                  : 'Shown instead of your name on listings, reviews and quotes. Leave blank to show your real name. Cannot be changed once set — choose carefully.'}
              </p>
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Address</h2>
          <p className="text-xs text-gray-400 mb-4">You can add this now or come back later — it's used for job matching and invoicing.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="form-label">Address Line 1 <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('addressLine1')} placeholder="House / flat number and street" />
            </div>
            <div className="sm:col-span-2">
              <label className="form-label">Address Line 2 <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('addressLine2')} placeholder="Village, district, etc." />
            </div>
            <div>
              <label className="form-label">City <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('city')} placeholder="City or town" />
            </div>
            <div>
              <label className="form-label">Postcode <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('postcode')} placeholder="e.g. SW1A 1AA" />
            </div>
          </div>
        </section>

        {/* Business */}
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Business</h2>
          <div className="flex items-center gap-2 mb-4">
            <input id="isBusiness" type="checkbox" className="rounded border-gray-300 text-blue-600" {...register('isBusinessAccount')} />
            <label htmlFor="isBusiness" className="text-sm text-gray-700">This is a business account</label>
          </div>
          {isBusiness && (
            <>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                All fields below are required to use Credit payment as a business customer.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="form-label">Business / Trading Name <span className="text-red-500">*</span></label>
                  <input className="form-input" {...register('businessName')} placeholder="Your company name" />
                </div>
                <div>
                  <label className="form-label">Companies House Number <span className="text-red-500">*</span></label>
                  <input className="form-input" {...register('companiesHouseNumber')} placeholder="e.g. 12345678" />
                </div>
                <div>
                  <label className="form-label">VAT Number <span className="text-red-500">*</span></label>
                  <input className="form-input" {...register('vatNumber')} placeholder="e.g. GB123456789" />
                </div>
                <div className="sm:col-span-2">
                  <label className="form-label">Director Name <span className="text-red-500">*</span></label>
                  <input className="form-input" {...register('directorName')} placeholder="Full name of company director" />
                </div>
              </div>
              <p className="text-xs text-gray-500">Trading address is collected in the Address section above.</p>
            </>
          )}
        </section>

        {/* Bank & Payment Details */}
        <section className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Bank &amp; Payment Details</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Used for payouts and invoicing. Sort code format: XX-XX-XX.
            Your Stripe Connected Account ID starts with <code className="bg-gray-100 px-1 rounded">acct_</code>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="form-label">Account Name <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('bankAccountName')} placeholder="e.g. John Smith or Acme Ltd" maxLength={100} />
            </div>
            <div>
              <label className="form-label">Sort Code <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('bankSortCode')} placeholder="e.g. 12-34-56" maxLength={10} />
            </div>
            <div>
              <label className="form-label">Account Number <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" {...register('bankAccountNumber')} placeholder="e.g. 12345678" maxLength={20} />
            </div>
            <div className="sm:col-span-2">
              <label className="form-label">Stripe Connected Account ID <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input font-mono text-sm" {...register('stripeConnectedAccountId')} placeholder="acct_xxxxxxxxxxxx" maxLength={100} />
            </div>
          </div>
        </section>

        {/* Submit */}
        <div className="flex justify-end">
          <button type="submit" disabled={!isDirty || mutation.isPending}
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Save className="w-4 h-4" />
            {mutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* ── Credit Account (own section, outside the main profile form) ── */}
      {isCustomer && (
        <section className="card p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-5 h-5 text-indigo-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Credit Account</h2>
            {profile?.creditApprovalStatus === 'Approved' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Approved</span>
            )}
            {profile?.creditApprovalStatus === 'Pending' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Pending review</span>
            )}
            {profile?.creditApprovalStatus === 'Rejected' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Not approved</span>
            )}
            {(!profile?.creditApprovalStatus || profile?.creditApprovalStatus === 'None') && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not requested</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Credit lets you settle after delivery rather than paying upfront. Available to individual and business customers
            subject to identity verification and Admin approval. Default payment method is <strong>Cash</strong>.
          </p>

          {profile?.creditApprovalStatus === 'Approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 text-sm text-green-800">
              <div className="flex items-center gap-2 font-medium mb-1">
                <BadgeCheck className="w-4 h-4" /> Credit account active
              </div>
              <p className="text-xs text-green-700">
                You can select Credit as your payment method when posting jobs.
                {profile.creditApprovedAt && (
                  <> Approved {new Date(profile.creditApprovedAt).toLocaleDateString('en-GB')}.</>
                )}
              </p>
            </div>
          )}

          {profile?.creditApprovalStatus === 'Pending' && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 text-sm text-blue-800">
              <Clock className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Request submitted — awaiting Admin review</p>
                <p className="text-xs text-blue-600 mt-0.5">Our team will review your application within 1–2 working days.</p>
              </div>
            </div>
          )}

          {profile?.creditApprovalStatus === 'Rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-800">
              <div className="flex items-center gap-2 font-medium mb-1">
                <XCircle className="w-4 h-4" /> Credit request not approved
              </div>
              {profile.creditRejectionReason && (
                <p className="text-xs text-red-700 mt-0.5">Reason: {profile.creditRejectionReason}</p>
              )}
              <p className="text-xs text-red-600 mt-1">You may re-submit a request after addressing the reason above.</p>
            </div>
          )}

          {!profile?.canRequestCredit && profile?.creditApprovalStatus !== 'Approved' && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm text-amber-800">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                {profile?.isBusinessAccount
                  ? 'Complete all business details (company name, registration number, VAT number, director name, and address) before requesting Credit.'
                  : 'Your identity document must be verified before you can request Credit. Submit a document in the Identity Verification section below.'}
              </p>
            </div>
          )}

          {creditError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-4 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {creditError}
            </div>
          )}

          {profile?.canRequestCredit
            && profile?.creditApprovalStatus !== 'Approved'
            && profile?.creditApprovalStatus !== 'Pending' && (
            <button
              type="button"
              onClick={() => creditMutation.mutate()}
              disabled={creditMutation.isPending}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creditMutation.isPending
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <CreditCard className="w-4 h-4" />
              }
              {creditMutation.isPending
                ? 'Submitting…'
                : profile?.creditApprovalStatus === 'Rejected'
                  ? 'Re-submit Credit Request'
                  : 'Request Credit Account'}
            </button>
          )}
        </section>
      )}

      {/* ── Identity Verification (any individual account) ──────────────
          Bazaar quoting/posting requires IdentityVerifiedAt regardless of
          role (BazaarQuoteService/BazaarListingService check it for any
          quoter/seller) - this used to be gated to Customer only, which
          left Carrier/Workshop accounts with no UI path to ever satisfy
          that gate. */}
      {!profile?.isBusinessAccount && (
        <section className="card overflow-hidden mt-6">
          {/* Header */}
          <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Identity Verification</h2>
            </div>
            <div className="shrink-0">
              {identityLatest ? (
                <StatusBadge status={identityLatest.reviewStatus} />
              ) : identitySubmitted ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  <Clock className="w-3 h-3" />Pending Review
                </span>
              ) : (
                <span className="text-xs text-gray-400">Not submitted</span>
              )}
            </div>
          </div>

          {/* Current document info */}
          {identityLatest && (
            <div className={`px-5 py-2.5 border-b text-xs flex flex-col gap-1 ${
              identityLatest.reviewStatus === 'Rejected' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'
            }`}>
              <div className="flex items-center gap-2 text-gray-600">
                <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                <span className="truncate font-medium">{identityLatest.originalFileName}</span>
                <span className="text-gray-400 shrink-0">
                  · {new Date(identityLatest.uploadedAt).toLocaleDateString('en-GB')}
                </span>
              </div>
              {profile?.identityDocumentType && (
                <p className="text-gray-500 pl-5">
                  Type: <strong>{profile.identityDocumentType}</strong>
                  {identityLatest.reviewStatus === 'Approved' && profile.identityVerifiedAt && (
                    <> · Verified {new Date(profile.identityVerifiedAt).toLocaleDateString('en-GB')}</>
                  )}
                </p>
              )}
              {identityLatest.reviewStatus === 'Rejected' && identityLatest.reviewNotes && (
                <p className="text-red-600 pl-5">
                  <span className="font-medium">Rejected: </span>{identityLatest.reviewNotes}
                </p>
              )}
            </div>
          )}

          <div className="px-5 py-4 space-y-3">
            <p className="text-xs text-gray-400">
              Submit a government-issued photo ID. Accepted formats: JPEG, PNG, PDF, DOCX (max 10 MB).
              Our team will verify and confirm within 1–2 working days.
            </p>

            {identityLatest?.reviewStatus === 'Approved' && profile?.identityVerifiedAt && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <BadgeCheck className="w-4 h-4" /> Identity verified
                </div>
                <p className="text-xs text-green-700">
                  Document type: <strong>{profile.identityDocumentType}</strong>
                  {' '}· Verified {new Date(profile.identityVerifiedAt).toLocaleDateString('en-GB')}
                </p>
              </div>
            )}

            {identitySubmitted && !identityLatest && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Document submitted — pending admin review</p>
                  <p className="text-xs text-blue-600 mt-0.5">Our team will verify your document within 1–2 working days.</p>
                </div>
              </div>
            )}

            {identityError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {identityError}
              </div>
            )}

            {identityLatest && (
              <p className="text-xs text-gray-500 font-medium">
                {identityLatest.reviewStatus === 'Rejected' ? 'Upload a corrected document:' : 'Upload a replacement:'}
              </p>
            )}

            <div>
              <label className="form-label">Document Type <span className="text-red-500">*</span></label>
              <select className="form-input" value={identityDocType} onChange={e => setIdentityDocType(e.target.value)}>
                <option value="">Select document type</option>
                <option value="DrivingLicence">Driving Licence</option>
                <option value="Passport">Passport</option>
                <option value="OtherGovtId">Other Government-Issued ID</option>
              </select>
            </div>

            <div>
              <label className="form-label">Document Number <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="form-input" placeholder="e.g. licence number, passport number"
                value={identityDocNumber} onChange={e => setIdentityDocNumber(e.target.value)} />
            </div>

            <div
              className="flex-1 min-w-0 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
              onClick={() => identityFileRef.current?.click()}
            >
              <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              {identityDocFile ? (
                <span className="text-gray-700 font-medium truncate">{identityDocFile.name}</span>
              ) : (
                <span className="text-gray-400">
                  {identityLatest ? 'Choose replacement file…' : 'Choose file…'}
                </span>
              )}
            </div>
            <input
              ref={identityFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={e => { setIdentityDocFile(e.target.files?.[0] ?? null); setIdentityError(null); }}
            />

            {/* DVLA Share Code — shown when Driving Licence is selected */}
            {identityDocType === 'DrivingLicence' && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  DVLA Share Code <span className="text-gray-400 font-normal">(optional — speeds up verification)</span>
                </p>
                <p className="text-xs text-gray-400 mb-2">
                  Get a share code from{' '}
                  <a href="https://www.gov.uk/view-driving-licence" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 underline">view-driving-licence</a>{' '}
                  and enter it below to let us verify your licence record directly.
                </p>
                <input
                  className="form-input uppercase tracking-widest text-sm"
                  placeholder="e.g. ABC123"
                  value={shareCode}
                  onChange={e => { setShareCode(e.target.value.toUpperCase()); setIdentityError(null); }}
                  maxLength={20}
                />
              </div>
            )}

            <button
              type="button"
              onClick={submitAll}
              disabled={identityUploading || !identityDocType || (!identityDocFile && !(identityDocType === 'DrivingLicence' && shareCode.trim()))}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:cursor-not-allowed"
            >
              {identityUploading
                ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <ShieldCheck className="w-3 h-3" />
              }
              {identityUploading ? 'Submitting…' : 'Submit'}
            </button>

            {shareCodeDone && !identitySubmitted && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />Share code submitted — our team will verify your record.
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Driving Licence ───────────────────────────────────────────── */}
      {isCarrier && (
        <section className="card p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Driving Licence</h2>
            {licLatest?.reviewStatus === 'Approved' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 inline mr-1" />Approved
              </span>
            )}
            {licLatest?.reviewStatus === 'Rejected' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                <XCircle className="w-3 h-3 inline mr-1" />Rejected
              </span>
            )}
            {licLatest && licLatest.reviewStatus !== 'Approved' && licLatest.reviewStatus !== 'Rejected' && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                <Clock className="w-3 h-3 inline mr-1" />Pending Review
              </span>
            )}
            {!licLatest && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not submitted</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Upload clear photos of both sides of your driving licence. Accepted formats: JPEG, PNG, WebP.
          </p>

          {licLatest?.reviewStatus === 'Approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 text-sm text-green-800">
              <div className="flex items-center gap-2 font-medium">
                <BadgeCheck className="w-4 h-4" /> Licence verified
              </div>
              <p className="text-xs text-green-700 mt-0.5">
                Submitted {new Date(licLatest.uploadedAt).toLocaleDateString('en-GB')}.
              </p>
            </div>
          )}

          {licSubmitted && !licLatest && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 text-sm text-blue-800">
              <Clock className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Photos submitted — pending admin review</p>
                <p className="text-xs text-blue-600 mt-0.5">Our team will verify your licence within 1–2 working days.</p>
              </div>
            </div>
          )}

          {licError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-4 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {licError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1.5">Front <span className="text-red-500">*</span></p>
              <div
                className="border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
                onClick={() => licFrontFileRef.current?.click()}
              >
                <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {licFrontFile ? (
                  <span className="text-gray-700 font-medium truncate">{licFrontFile.name}</span>
                ) : (
                  <span className="text-gray-400">Choose file…</span>
                )}
              </div>
              <input
                ref={licFrontFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => { setLicFrontFile(e.target.files?.[0] ?? null); setLicError(null); setLicSubmitted(false); }}
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1.5">Back <span className="text-gray-400 font-normal">(optional)</span></p>
              <div
                className="border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
                onClick={() => licBackFileRef.current?.click()}
              >
                <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {licBackFile ? (
                  <span className="text-gray-700 font-medium truncate">{licBackFile.name}</span>
                ) : (
                  <span className="text-gray-400">Choose file…</span>
                )}
              </div>
              <input
                ref={licBackFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => { setLicBackFile(e.target.files?.[0] ?? null); setLicError(null); }}
              />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={submitLicence}
              disabled={!licFrontFile || licUploading}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {licUploading
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Upload className="w-4 h-4" />
              }
              {licUploading ? 'Uploading…' : 'Upload Licence'}
            </button>
          </div>
        </section>
      )}

      {/* ── DVLA Licence Verification ─────────────────────────────────── */}
      {isCarrier && (
        <section className="card p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">DVLA Licence Verification</h2>
            {dvlaStatus?.verified && !dvlaStatus.isOverdue && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Verified</span>
            )}
            {dvlaStatus?.isOverdue && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Overdue</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Required yearly. Get a share code from GOV.UK and submit it below — our team will verify your record.
          </p>

          {dvlaStatus?.verified && !dvlaStatus.isOverdue && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 text-sm text-green-800">
              <div className="flex items-center gap-2 font-medium mb-1">
                <CheckCircle className="w-4 h-4" /> Licence verified
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-1">
                {dvlaStatus.carrierFullName && <span>Name: <strong>{dvlaStatus.carrierFullName}</strong></span>}
                {dvlaStatus.licenceNumber  && <span>Licence: <strong>{dvlaStatus.licenceNumber}</strong></span>}
                {dvlaStatus.drivingStatus  && <span>Status: <strong>{dvlaStatus.drivingStatus}</strong></span>}
                {dvlaStatus.licenceValidTo && <span>Expires: <strong>{dvlaStatus.licenceValidTo}</strong></span>}
                <span>Points: <strong>{dvlaStatus.endorsementPoints ?? 0}</strong></span>
                {dvlaStatus.nextCheckDue && (
                  <span>Next check: <strong>{new Date(dvlaStatus.nextCheckDue).toLocaleDateString('en-GB')}</strong></span>
                )}
              </div>
              {dvlaStatus.pdfBlobUri && (
                <a href={dvla.getPdfUrl(dvlaStatus.pdfBlobUri)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-green-700 underline mt-2">
                  <ExternalLink className="w-3 h-3" /> View licence summary PDF
                </a>
              )}
            </div>
          )}

          {dvlaStatus?.uploadedAt && !dvlaStatus.verified && !dvlaStatus.isOverdue && !dvlaStatus.pdfBlobUri && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 text-sm text-blue-800">
              <div className="flex items-center gap-2 font-medium mb-1">
                <ShieldCheck className="w-4 h-4" /> Share code submitted — awaiting verification
              </div>
              <p className="text-xs text-blue-700">
                Our team will verify your driving record using the share code you provided. This usually takes 1–2 working days.
              </p>
            </div>
          )}

          {dvlaStatus?.uploadedAt && !dvlaStatus.verified && !dvlaStatus.isOverdue && !!dvlaStatus.pdfBlobUri && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm text-amber-800">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertCircle className="w-4 h-4" /> Verification failed — please resubmit
              </div>
              <p className="text-xs text-amber-700">We could not read your driving record. Please submit a new share code below.</p>
            </div>
          )}

          {shareCodeError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-4 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {shareCodeError}
            </div>
          )}

          {(!dvlaStatus?.verified || dvlaStatus?.isOverdue) && !(dvlaStatus?.uploadedAt && !dvlaStatus.pdfBlobUri && !dvlaStatus.isOverdue) && (
            <div className="border border-dashed border-gray-200 rounded-lg px-4 py-4">
              <p className="text-xs text-gray-500 font-medium mb-1">Step 1 — get a share code from GOV.UK</p>
              <a href="https://www.viewdrivingrecord.service.gov.uk" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-700 underline mb-1">
                <ExternalLink className="w-3 h-3" /> viewdrivingrecord.service.gov.uk
              </a>
              <p className="text-xs text-gray-400 mb-4">
                Log in → "Share your driving record" → generate a check code (e.g. BNRN 88FK).
              </p>
              <p className="text-xs text-gray-500 font-medium mb-2">Step 2 — enter your share code below</p>
              <div className="flex items-center gap-2">
                <input type="text" className="form-input flex-1 uppercase tracking-widest text-sm"
                  placeholder="e.g. BNRN 88FK" value={shareCode} maxLength={20}
                  onChange={e => { setShareCode(e.target.value.toUpperCase()); setShareCodeError(null); }} />
                <button type="button"
                  onClick={() => shareCodeMutation.mutate(shareCode)}
                  disabled={!shareCode.trim() || shareCodeMutation.isPending}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                  {shareCodeMutation.isPending ? 'Submitting…' : 'Submit'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Our team will use this code to verify your licence. You'll be notified once done.
              </p>
            </div>
          )}
        </section>
      )}

      {/* ── Membership Access ─────────────────────────────────────────── */}
      {(isCarrier || isWorkshop) && (
        <section className="card p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Membership Access</h2>
            {eligibility?.isEligible ? (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Eligible</span>
            ) : (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Action Required</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-4">
            To submit quotes on TransitDeal you must satisfy <strong>at least one</strong> of the three routes below.
            {isCarrier && ' It is also recommended that all registered vehicles have admin-verified H&R Insurance and MOT certificates.'}
          </p>

          {/* Vehicle verification prerequisite — Carrier only (Workshop has no fleet) */}
          {isCarrier && (() => {
            const noVehicles   = !eligibility?.hasActiveVehicles;
            const allVerified  = eligibility?.allVehiclesVerified;
            const pendingDocs  = eligibility?.hasActiveVehicles && !allVerified;
            const alreadyOk    = eligibility?.isEligible;
            const cardClass    = noVehicles ? 'border-gray-200 bg-gray-50'
                               : allVerified ? 'border-green-300 bg-green-50'
                               : alreadyOk  ? 'border-amber-200 bg-amber-50'
                               : 'border-red-200 bg-red-50';
            const badgeClass   = noVehicles ? 'bg-gray-100 text-gray-500'
                               : allVerified ? 'bg-green-100 text-green-700'
                               : alreadyOk  ? 'bg-amber-100 text-amber-700'
                               : 'bg-red-100 text-red-700';
            const badgeLabel   = noVehicles ? 'No Vehicles'
                               : allVerified ? 'All Verified'
                               : alreadyOk  ? 'Recommended'
                               : 'Action Required';
            const icon = noVehicles
              ? <span className="w-4 h-4 rounded-full border-2 border-gray-400 shrink-0 inline-block" />
              : allVerified
                ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                : alreadyOk
                  ? <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                  : <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />;
            return (
              <div className={`rounded-lg border px-4 py-3 mb-3 ${cardClass}`}>
                <div className="flex items-center gap-2">
                  {icon}
                  <span className="text-sm font-semibold text-gray-800">Vehicle Documents</span>
                  <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${badgeClass}`}>
                    {badgeLabel}
                  </span>
                </div>
                <p className="text-xs mt-1 pl-6 text-gray-600">
                  {noVehicles
                    ? <>No vehicles registered. If you add vehicles in <Link to="/vehicles" className="text-blue-600 hover:underline">Vehicles</Link>, their MOT is checked automatically with the DVSA and each will need verified H&amp;R Insurance.</>
                    : allVerified
                      ? 'All your vehicles have verified H&R Insurance and a current MOT.'
                      : pendingDocs && alreadyOk
                        ? <>You can already submit quotes. When ready, upload H&amp;R Insurance in <Link to="/vehicles" className="text-blue-600 hover:underline">Vehicles</Link> for verification - MOT is confirmed automatically with the DVSA.</>
                        : <>Add each vehicle in <Link to="/vehicles" className="text-blue-600 hover:underline">Vehicles</Link> - we confirm its MOT with the DVSA - then upload H&amp;R Insurance for verification.</>
                  }
                </p>
              </div>
            );
          })()}

          {depositStatus === 'success' && (
            <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
              <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Payment received — your deposit is being confirmed. Your membership status will update momentarily.
            </div>
          )}
          {depositStatus === 'cancelled' && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Payment was cancelled. You can try again whenever you're ready.
            </div>
          )}

          <div className="space-y-3">
            {/* Route 1 — External Rating */}
            <div className={`rounded-lg border px-4 py-3 ${eligibility?.hasQualifyingRating ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                {eligibility?.hasQualifyingRating
                  ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                  : <span className="w-4 h-4 rounded-full border-2 border-gray-400 shrink-0 inline-block" />
                }
                <span className="text-sm font-semibold text-gray-800">Route 1 — 4★+ External Rating</span>
                {eligibility?.hasQualifyingRating && (
                  <span className="ml-auto text-xs font-medium text-green-700 shrink-0">
                    {eligibility.highestApprovedRating?.toFixed(1)} ★ verified
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 pl-6">
                {eligibility?.hasQualifyingRating
                  ? 'Your verified external rating is displayed to customers on every bid — and qualifies for this route.'
                  : <>Your external ratings are always visible to customers on every bid regardless of membership status. Upload a screenshot in <strong>External Ratings</strong> below — a verified rating of 4★ or above also satisfies this route.</>
                }
              </p>
            </div>

            {/* Route 2 — Referee Code */}
            <div className={`rounded-lg border px-4 py-3 ${eligibility?.isReferred ? 'border-green-300 bg-green-50' : 'border-blue-100 bg-blue-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                {eligibility?.isReferred
                  ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                  : <Key className="w-4 h-4 text-blue-600 shrink-0" />
                }
                <span className="text-sm font-semibold text-gray-800">Route 2 — Referee Code</span>
                {eligibility?.isReferred && (
                  <span className="ml-auto text-xs font-medium text-green-700 shrink-0">Referred ✓</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2 pl-6">
                {eligibility?.isReferred
                  ? 'You were referred by an existing TransitDeal member — this route is satisfied.'
                  : 'Enter a referee code from an existing member who has completed at least one job on TransitDeal.'
                }
              </p>
              {!eligibility?.isReferred && (
                <div className="pl-6 space-y-2">
                  {refereeCodeError && (
                    <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{refereeCodeError}
                    </div>
                  )}
                  {refereeCodeSuccess && (
                    <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />Referee code accepted — refreshing your eligibility status…
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="form-input flex-1 uppercase tracking-widest text-sm"
                      placeholder="Enter referee code"
                      value={refereeCode}
                      maxLength={50}
                      onChange={e => {
                        setRefereeCode(e.target.value.toUpperCase());
                        setRefereeCodeError(null);
                        setRefereeCodeSuccess(false);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => referralCodeMutation.mutate(refereeCode.trim())}
                      disabled={!refereeCode.trim() || referralCodeMutation.isPending}
                      className="btn-primary inline-flex items-center gap-2 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {referralCodeMutation.isPending ? 'Applying…' : 'Apply'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Route 3 — Security Deposit */}
            <div className={`rounded-lg border px-4 py-3 ${
              eligibility?.hasSecurityDeposit
                ? 'border-green-300 bg-green-50'
                : eligibility?.hasPendingDeposit
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {eligibility?.hasSecurityDeposit
                  ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                  : eligibility?.hasPendingDeposit
                    ? <Loader2 className="w-4 h-4 text-amber-600 shrink-0 animate-spin" />
                    : <span className="w-4 h-4 rounded-full border-2 border-gray-400 shrink-0 inline-block" />
                }
                <span className="text-sm font-semibold text-gray-800">Route 3 — £500 Security Deposit</span>
                {eligibility?.hasSecurityDeposit && (
                  <span className="ml-auto text-xs font-medium text-green-700 shrink-0">Member ✓</span>
                )}
                {eligibility?.hasPendingDeposit && (
                  <span className="ml-auto text-xs font-medium text-amber-700 shrink-0">Payment processing…</span>
                )}
              </div>

              {/* No deposit yet — show Pay button */}
              {!eligibility?.hasSecurityDeposit && !eligibility?.hasPendingDeposit && (
                <div className="pl-6" data-testid="deposit-payment-choice">
                  <p className="text-xs text-gray-500 mb-3">
                    A fully refundable deposit of <strong>£{(depositQuote?.amount ?? 500).toFixed(2)}</strong>, held
                    only to cover disputes and returned after 10 successful jobs and a 4★+ rating. The deposit held
                    is the same either way — paying by card simply carries the card processing fee.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => depositCheckoutMutation.mutate()}
                      disabled={depositCheckoutMutation.isPending}
                      data-testid="deposit-pay-card"
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="w-4 h-4" />
                      {depositCheckoutMutation.isPending
                        ? 'Redirecting to Stripe…'
                        : `Pay £${(depositQuote?.cardTotal ?? 510).toFixed(2)} by card`}
                    </button>

                    <button
                      type="button"
                      onClick={() => depositBankTransferMutation.mutate()}
                      disabled={depositBankTransferMutation.isPending}
                      data-testid="deposit-pay-bank"
                      className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {depositBankTransferMutation.isPending
                        ? 'Preparing…'
                        : `Pay £${(depositQuote?.bankTransferTotal ?? 500).toFixed(2)} by bank transfer — free`}
                    </button>
                  </div>

                  {depositQuote && depositQuote.cardFee > 0 && (
                    <p className="text-xs text-gray-500 mt-2" data-testid="deposit-card-fee-note">
                      Card adds £{depositQuote.cardFee.toFixed(2)} on top. Bank transfer is free.
                    </p>
                  )}

                  {bankTransferInstruction && (
                    <div className="mt-3 text-sm border border-blue-200 bg-blue-50 rounded-lg p-3"
                         data-testid="deposit-bank-instruction">
                      <p className="font-semibold text-blue-900 mb-1">
                        Send £{bankTransferInstruction.amount.toFixed(2)} by bank transfer
                      </p>
                      <p className="text-blue-900">
                        Quote reference{' '}
                        <span className="font-mono font-semibold" data-testid="deposit-bank-reference">
                          {bankTransferInstruction.reference}
                        </span>. You become a member once our team confirms the funds have arrived.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Payment initiated — waiting for webhook */}
              {eligibility?.hasPendingDeposit && (
                <p className="text-xs text-amber-700 pl-6">
                  Your payment is being verified. This usually takes a few seconds — refresh the page shortly.
                </p>
              )}

              {/* Deposit confirmed */}
              {eligibility?.hasSecurityDeposit && (
                <div className="pl-6 space-y-2">
                  <p className="text-xs text-gray-600">
                    Your £500 refundable deposit is confirmed. You are a full member and can submit quotes.
                  </p>
                  {eligibility.isDepositRefundUnlocked ? (
                    <div className="flex items-center gap-2 text-xs text-green-700 bg-green-100 rounded-md px-3 py-2">
                      <LockOpen className="w-3.5 h-3.5 shrink-0" />
                      <span>Your deposit has been unlocked for refund — contact support to arrange the return.</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-100 rounded-md px-3 py-2">
                      <Lock className="w-3.5 h-3.5 shrink-0" />
                      <span>Deposit locked — refundable after 10 successful jobs and a 4★+ rating. Admin will approve the unlock.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── External Ratings ─────────────────────────────────────────── */}
      {!isCustomer && (
        <section className="mt-6 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">External Ratings</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Verified ratings from Checkatrade, Trustpilot, or Google are shown to customers on every bid and job — independently of membership status.
              An approved rating of 4★ or above also qualifies for Membership Access Route 1.
            </p>
          </div>
          <ExternalRatingsSection getToken={getToken} userRole={docUserRole} />
        </section>
      )}

      {/* ── Danger Zone ───────────────────────────────────────────────── */}
      <section className="card p-5 mt-6 border border-red-200">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5 text-red-600" />
          <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide">Danger Zone</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Deleting your account deactivates it immediately — you will not be able to sign in again, even
          with the same email address. This cannot be undone by you; only an admin can restore or
          permanently delete the account.
        </p>
        <button
          type="button"
          onClick={handleDeleteAccount}
          disabled={deleteAccountMutation.isPending}
          className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:cursor-not-allowed"
        >
          {deleteAccountMutation.isPending
            ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <Trash2 className="w-3 h-3" />
          }
          {deleteAccountMutation.isPending ? 'Deleting…' : 'Delete Account'}
        </button>
      </section>

    </div>
  );
}
